import {
  BarChart2,
  CalendarDays,
  GraduationCap,
  Heart,
  Info,
  Lightbulb,
  LayoutDashboard,
  LogIn,
  Presentation,
  Shield,
  Trophy,
  Users,
} from 'lucide-react';
import { can, grantedPermissions, hasAdminAccess, hasModule } from '../../shared/hooks/useModuleAccess';

export function getSidebarSections(user, t, orgEnabledModules: string[] | null = null) {
  const isOrgModuleEnabled = (moduleKey: string | null) => {
    if (!moduleKey) return true;
    if (orgEnabledModules === null) return true;
    return orgEnabledModules.includes(moduleKey);
  };

  const lmsItems = [
    { label: t('navigation.dashboard'), to: '/dashboard', icon: LayoutDashboard, module: null },
    { label: t('navigation.learningHub'), to: '/learning', icon: GraduationCap, module: 'learning' },
    { label: t('navigation.kudos'), to: '/kudos', icon: Heart, module: 'kudos' },
    { label: t('navigation.leaderboard'), to: '/leaderboard', icon: Trophy, module: 'learning' },
    // HR's Reward & Recognition programme: the use cases everyone submits (part E) and
    // the learning proposals HR marks implemented (part F), as two tabs of one page.
    // Their detail pages keep /use-cases/:id and /proposals/:id, so those light it too.
    { label: t('navigation.contribute'), to: '/contribute', icon: Lightbulb, module: null, activeFor: ['/use-cases', '/proposals'] },
  ].filter((item) => isOrgModuleEnabled(item.module) && (!item.module || hasModule(user, item.module)));

  const exploreItems = [
    { label: t('navigation.events'), to: '/events', icon: CalendarDays, module: 'events' },
    { label: t('navigation.workshops'), to: '/workshops', icon: Presentation, module: 'workshops' },
    { label: t('navigation.people'), to: '/people', icon: Users, module: 'people' },
    { label: t('navigation.about'), to: '/about', icon: Info, module: null },
  ].filter((item) => isOrgModuleEnabled(item.module) && (!item.module || !user || hasModule(user, item.module)));

  const accountItems = user
    ? [
        ...(user.isAdmin || user.isHr ? [
          { label: t('navigation.adminCenter'), to: '/admin', icon: Shield },
          { label: t('navigation.analytics'), to: '/analytics', icon: BarChart2 },
        ] : [
          // Given a permission (a moderator, an event approver): Admin with their part only (ADR-043).
          ...(hasAdminAccess(user) && grantedPermissions(user).some((p) => p !== 'view_analytics')
            ? [{ label: t('navigation.adminCenter'), to: '/admin', icon: Shield }] : []),
          ...(can(user, 'view_analytics') ? [{ label: t('navigation.analytics'), to: '/analytics', icon: BarChart2 }] : []),
        ]),
      ]
    : [{ label: t('navigation.logIn'), to: '/login', icon: LogIn }];

  const managementLabel = user && (user.isAdmin || user.isHr)
    ? t('navigation.management')
    : t('navigation.account');

  return { lmsItems, exploreItems, accountItems, managementLabel };
}

/**
 * The line under the page title in the topbar.
 *
 * There used to be exactly one, header.pageSubtitle, and it read "Plan moments
 * people actually want to join" -- so the Leaderboard, the Admin Center and the
 * Analytics reports all described themselves as an events page.
 *
 * A route with nothing worth saying gets nothing; a subtitle that repeats the
 * title is worse than none.
 */
export function getRouteSubtitles(t) {
  return {
    '/events': t('header.pageSubtitle'),
    '/dashboard': t('routes.subtitles.dashboard'),
    '/learning': t('routes.subtitles.learning'),
    '/kudos': t('routes.subtitles.kudos'),
    '/leaderboard': t('routes.subtitles.leaderboard'),
    '/people': t('routes.subtitles.people'),
    // /community renders the same directory. Without an entry here the header
    // fell back to the application name, which said nothing about the page.
    '/community': t('people.communityCopy'),
    '/workshops': t('routes.subtitles.workshops'),
    '/admin': t('routes.subtitles.admin'),
    '/analytics': t('routes.subtitles.analytics'),
    '/about': t('routes.subtitles.about'),
    '/profile': t('routes.subtitles.profile'),
    '/points': t('routes.subtitles.points'),
    '/contribute': t('routes.subtitles.contribute'),
    '/profile/certificates': t('routes.subtitles.certificates'),
    '/values': t('routes.subtitles.values'),
  };
}

export function getRouteLabels(t) {
  return {
    '/': t('routes.dashboard'),
    '/events': t('routes.home'),
    '/dashboard': t('routes.dashboard'),
    '/learning': t('routes.learning'),
    '/kudos': t('routes.kudos'),
    '/leaderboard': t('routes.leaderboard'),
    '/about': t('routes.about'),
    '/people': t('routes.people'),
    '/community': t('people.communityTitle'),
    '/workshops': t('routes.workshops'),
    '/profile': t('routes.profile'),
    '/points': t('routes.points'),
    '/contribute': t('routes.contribute'),
    '/profile/certificates': t('routes.certificates'),
    '/values': t('routes.values'),
    '/admin': t('routes.admin'),
    '/analytics': t('routes.analytics'),
    // The notifications page read "Grow", the fallback, in the top bar.
    '/notifications': t('notifications.title'),
    '/403': t('errorPages.forbiddenTitle'),
    '/login': t('routes.login'),
    '/signup': t('routes.signup'),
  };
}
